import { Inject, Injectable } from '@angular/core'
import { catchError, first, map, mergeMap, retryWhen, switchMap, timeout } from 'rxjs/operators'
import { forkJoin, Observable, of, throwError } from 'rxjs'
import { Router } from '@angular/router'
import { MatDialog } from '@angular/material/dialog'
import { L10N_LOCALE, L10nLocale } from 'angular-l10n'
import { LbHttpService } from '../lb-http.service'
import { LoadingService } from '../utils/loading.service'
import { LocalStorageService } from '../utils/local-storage.service'
import { TimeslotsService } from './timeslots.service'
import { EndpointService } from './endpoint.service'
import { TicketService } from './ticket.service'
import { QueueStateService } from './queue-state.service'
import { ErrorService } from '../utils/error.service'
import { fromPromise } from 'rxjs/internal-compatibility'
import { ConfigService } from '../config.service'
import { HttpClient } from '@angular/common/http'
import { NavigateService } from '../utils/navigate.service'
import { LbUtilsService } from '@affluences/lb-utils-front'
import {QueueEndpointService} from './queue-endpoint.service';

@Injectable( {
    providedIn: 'root'
} )
export class OperatorService {

    tokenValid: boolean = false
    endpointSubscription: any
    autoLogOut: boolean = true

    constructor (
        @Inject( L10N_LOCALE ) public locale: L10nLocale,
        private router: Router,
        public dialog: MatDialog,
        private http: HttpClient,
        private lbHttpService: LbHttpService,
        private loader: LoadingService,
        private errorService: ErrorService,
        private localStorageService: LocalStorageService,
        private timeslotsService: TimeslotsService,
        private queueEndpointService: QueueEndpointService,
        public endpointService: EndpointService,
        public ticketService: TicketService,
        public configService: ConfigService,
        public navigateService: NavigateService,
        public queueStateService: QueueStateService,
        public lbUtilsService: LbUtilsService
    ) {
        this.init()
    }

    public setAutoLogOut ( value: boolean ) {
        this.autoLogOut = value
    }

    unsubscribe ( all = false ) {
        if ( this.endpointSubscription ) {
            this.endpointSubscription.unsubscribe()
        }
        if ( all ) {
            this.ticketService.unsubscribe()
            this.queueStateService.unsubscribe()
            this.queueEndpointService.unsubscribe()
        }
    }

    getOperatorStatus () {
        const bearer = this.localStorageService.getOperatorBearer()
        const bearerIsset = this.localStorageService.issetBearer('validation')
        this.tokenValid = this.localStorageService.tokenIsValid('validation')
        if (bearer && bearerIsset) {
            const decodedBearer: any = this.lbUtilsService.decodeJwt(bearer)
            if ( this.tokenValid ) {
                if (decodedBearer && decodedBearer.payload && typeof decodedBearer.payload.endpointId === 'string' && decodedBearer.payload.endpointId.length > 0) {
                    return 'LOGGED'
                } else {
                    return 'PRE_LOGGED'
                }
            } else {
                return 'NOT_LOGGED'
            }
        } else {
            return 'NOT_LOGGED'
        }
    }

    init () {
        const bearer = this.localStorageService.getOperatorBearer()
        const bearerIsset = this.localStorageService.issetBearer('validation')
        this.tokenValid = this.localStorageService.tokenIsValid('validation')
        if (bearer && bearerIsset) {
            if ( this.tokenValid ) {
                if (this.getOperatorStatus() === 'LOGGED') {
                    this.startEndpointSubscription()
                }
            } else {
               this.logout()
            }
        }
    }

    startEndpointSubscription () {
        this.endpointSubscription = this.endpointService.$endpointStateEvent.subscribe( ( endpoint ) => {
            this.errorService.logDebug( 'Event on endpoint on operatorService', {endpoint, logged: this.getOperatorStatus() === 'LOGGED', autoLogOut: this.autoLogOut} )
            if ( endpoint.state === 2 && this.getOperatorStatus() === 'LOGGED' && this.autoLogOut ) {
                this.errorService.forceLogOut()
                this.logout( { closeEndpoint: false, deleteEndpointTimeslots: { delete: false }, redirect: false } )
            }
        }, ( err ) => {
            this.errorService.logError( 'Error on endpoint SSE', err )
        } )
        this.endpointService.loadEndpointStateIfNotLoaded()
    }

    onFocus () {
        this.tokenValid = this.localStorageService.tokenIsValid( 'validation' )
        if ( this.getOperatorStatus() === 'LOGGED' ) {
            if ( !this.tokenValid ) {
                this.logout()
            } else {
                this.endpointService.sendEndpointStates()
                this.ticketService.reloadTicketToDisplay()
                this.queueStateService.sendQueueStates()
            }
        }
    }

    public preLogin ( preLogin: { loginId: string, password: string} | { ssoToken: string, subDomain?: string, redirect_uri: string } ) {
        return this.httpPreLogin( preLogin ).pipe(
            switchMap( ( res: any ) => {
                this.localStorageService.setOperatorBearer( res['jwtToken'] )
                return of( true )
            })
        )
    }

    login (
        endpointId: string,
        login: {loginId: string, password: string} | { ssoToken: string, subDomain?: string, redirect_uri: string },
        typeOfLogin: 'LOGIN' | 'ASSIGN',
        actions?: {
            openEndpoint?: boolean,
            getPlaceTimeslots?: { load: boolean, placeId: string },
            getOperatorAccount?: boolean,
            getEndpointTimeslots?: boolean,
            setEndpointTimeslots?: { set: boolean, timeslots?: { startAt: string, endAt: string }[], setToPlaceTimeslots?: boolean, onlyIfEndpointTimeslotsNotExists?: boolean }
        },
        mode?: string
    ) {

        this.loader.show()
        let openEndpoint: boolean = true
        let getPlaceTimeslots: boolean = true
        let getOperatorAccount: boolean = true
        let getEndpointTimeslots: boolean = true
        let setEndpointTimeslots: boolean = false
        let placeId: string = ''

        if ( actions ) {
            if ( typeof (actions.openEndpoint) === 'boolean' ) {
                openEndpoint = actions.openEndpoint
            }
            if ( actions.getPlaceTimeslots && typeof (actions.getPlaceTimeslots.load) === 'boolean' ) {
                getPlaceTimeslots = actions.getPlaceTimeslots.load
                placeId = actions.getPlaceTimeslots.placeId
            }
            if ( typeof (actions.getOperatorAccount) === 'boolean' ) {
                getOperatorAccount = actions.getOperatorAccount
            }
            if ( typeof (actions.getEndpointTimeslots) === 'boolean' ) {
                getEndpointTimeslots = actions.getEndpointTimeslots
            }
            if ( typeof (actions.setEndpointTimeslots) === 'object' ) {
                if ( actions.setEndpointTimeslots.set && (
                    (typeof (actions.setEndpointTimeslots.timeslots) === 'object' && actions.setEndpointTimeslots.timeslots.length > 0)
                    || actions.setEndpointTimeslots.setToPlaceTimeslots
                ) ) {
                    setEndpointTimeslots = true
                }
            }
        }

        let ob: Observable<any>
        if (typeOfLogin === 'LOGIN') {
            ob = this.httpLogin(endpointId, login)
        } else {
            if (this.localStorageService.getOperatorBearer()) {
                const decodedBearer: any = this.lbUtilsService.decodeJwt(this.localStorageService.getOperatorBearer())
                if (decodedBearer.payload && !decodedBearer.payload.endpointId) {
                    ob = this.httpAssignToEndpoint(endpointId)
                }
            }
        }

        if (ob) {
            return ob.pipe(
                switchMap((res: any) => {
                    this.localStorageService.setOperatorBearer(res['jwtToken'])
                    return of(true)
                }),
                switchMap((res2: any) => {
                    return this.setLogin(openEndpoint, getPlaceTimeslots, getEndpointTimeslots, getOperatorAccount, setEndpointTimeslots, placeId, endpointId, actions, mode)
                })
            )
        } else {
            return this.setLogin(openEndpoint, getPlaceTimeslots, getEndpointTimeslots, getOperatorAccount, setEndpointTimeslots, placeId, endpointId, actions, mode)
        }
    }

    setLogin(
        openEndpoint: boolean,
        getPlaceTimeslots: boolean,
        getEndpointTimeslots: boolean,
        getOperatorAccount: boolean,
        setEndpointTimeslots: boolean,
        placeId: string,
        endpointId: string,
        actions?: {
            openEndpoint?: boolean,
            getPlaceTimeslots?: { load: boolean, placeId: string },
            getOperatorAccount?: boolean,
            getEndpointTimeslots?: boolean,
            setEndpointTimeslots?: { set: boolean, timeslots?: { startAt: string, endAt: string }[], setToPlaceTimeslots?: boolean, onlyIfEndpointTimeslotsNotExists?: boolean }
        },
        mode?: string
    ): Observable<any> {
        const obs: any = [this.httpGetOperatorRoles(true)]
        if (openEndpoint) {
            obs.push(this.endpointService.httpOpenEndpoint(true, mode, true))
        }
        if (getPlaceTimeslots) {
            obs.push(this.timeslotsService.httpGetPlaceTimeslots(placeId, true))
        }
        if (getEndpointTimeslots) {
            obs.push(this.timeslotsService.getEndpointTimeslotsOfTheDay(true))
        }
        if (getOperatorAccount) {
            obs.push(this.getOperatorAccount(true))
        }
        return forkJoin(...obs).pipe(
            switchMap((res: any) => {
                this.startEndpointSubscription()

                return this.endpointService.httpGetEndpoint(endpointId)
            }),
            switchMap((endpoint: any) => {
                this.localStorageService.setSelectedEndpoint(endpoint)
                this.endpointService.sendEndpointStates()

                if (setEndpointTimeslots) {
                    const endpointTimeslots = this.localStorageService.getCurrentEndpointTimeslots()
                    if (
                        endpointTimeslots.length <= 0 ||
                        (endpointTimeslots.length > 0 && !actions.setEndpointTimeslots.onlyIfEndpointTimeslotsNotExists)
                    ) {
                        if (typeof (actions.setEndpointTimeslots.timeslots) === 'object' && actions.setEndpointTimeslots.timeslots.length > 0) {
                            return this.timeslotsService.httpSetEndpointTimeslots(actions.setEndpointTimeslots.timeslots, true)
                        } else if (actions.setEndpointTimeslots.setToPlaceTimeslots) {
                            const placesTimeslots = this.localStorageService.getCurrentPlaceTimeslots()
                            if (placesTimeslots && placesTimeslots.length > 0) {
                                return this.timeslotsService.httpSetEndpointTimeslots(this.localStorageService.getCurrentPlaceTimeslots(), true)
                            }
                        }
                    }
                }
                return of(true)
            })
        )
    }

    logout ( actions?: {
                 closeEndpoint?: boolean,
                 deleteEndpointTimeslots?: { delete: boolean, onlyCurrentTimeslots?: boolean },
                 deleteStorage?: boolean
                 redirect?: boolean
             }
    ) {

        const endpoint = this.localStorageService.getSelectedEndpoint()
        this.loader.show()
        this.unsubscribe( true )
        this.endpointService.unsubscribe()
        let closeEndpoint: boolean = false
        let deleteEndpointTimeslots: boolean = false
        let deleteOnlyCurrentTimeslots: boolean = false
        let redirect: boolean = true
        let deleteStorage: boolean = false

        if ( actions ) {
            if ( typeof (actions.closeEndpoint) === 'boolean' ) {
                closeEndpoint = actions.closeEndpoint
            }
            if ( typeof (actions.redirect) === 'boolean' ) {
                redirect = actions.redirect
            }
            if ( typeof (actions.deleteStorage) === 'boolean' ) {
                deleteStorage = actions.deleteStorage
            }
            if ( endpoint && endpoint.config && endpoint.config.editTimeslotsAllowed ) {
                if ( typeof (actions.deleteEndpointTimeslots) === 'object' ) {
                    if ( typeof (actions.deleteEndpointTimeslots.delete) === 'boolean' ) {
                        deleteEndpointTimeslots = actions.deleteEndpointTimeslots.delete
                    }
                    if ( typeof (actions.deleteEndpointTimeslots.onlyCurrentTimeslots) === 'boolean' ) {
                        // TODO: make it work for Air france case
                        deleteOnlyCurrentTimeslots = actions.deleteEndpointTimeslots.onlyCurrentTimeslots
                    }
                }
            }
        }

        const endpointId = this.localStorageService.getSelectedEndpoint()
        && this.localStorageService.getSelectedEndpoint().endpointId
        && this.localStorageService.getSelectedEndpoint().endpointId !== '' ? this.localStorageService.getSelectedEndpoint().endpointId : null
        const obs = []
        if ( endpointId && this.localStorageService.tokenIsValid( 'validation' ) ) {
            if ( closeEndpoint ) {
                obs.push( this.endpointService.httpOpenEndpoint( false ) )
            }
            if ( deleteEndpointTimeslots ) {
                obs.push( this.timeslotsService.httpDeleteEndpointTimeslots() )
                // TODO: deleteOnlyCurrentTimeslots
            }
        }

        if ( obs.length === 0 ) {
            obs.push( of( true ) )
        }

        return forkJoin( obs ).pipe(
            switchMap( () => {
                if ( endpointId && this.localStorageService.tokenIsValid( 'validation' ) ) {
                    return this.httpLogout()
                } else {
                    return of( true )
                }
            } ),
            first()
        ).subscribe( () => {
            this.localStorageService.logOut()
            if ( deleteStorage ) {
                window.localStorage.clear()
                window.sessionStorage.clear()
            }
            this.loader.hide()
            if ( redirect ) {
                return this.navigateService.goToHome(this.localStorageService.getApplicationId())
            }
        }, async ( err ) => {
            this.errorService.logError( 'Error while log out the operator global', err )
            this.localStorageService.clear()
            this.localStorageService.logOut()
            // await this.translationService.init()
            await this.loader.hide()
            if ( redirect ) {
                return this.navigateService.goToHome(this.localStorageService.getApplicationId())
            }
        } )
    }

    private httpPreLogin ( preLogin: { loginId: string, password: string} | { ssoToken: string, subDomain?: string, redirect_uri?: string } ) {
        const url = this.configService.preLogin
        const body = preLogin

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( false, false, 'validation' ) ).pipe(
            timeout( this.configService.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                return this.http.post( url, body, httpOptions )
            }),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while login operator', error )
                return throwError( error )
            } )
        )
    }

    private httpLogin ( endpointId, login: {loginId: string, password: string} | { ssoToken: string, subDomain?: string, redirect_uri: string } ) {
        let url = this.configService.loginOperator
        url = url.replace( '{endpointId}', endpointId )
        const body = login

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( false, false, 'validation' ) ).pipe(
            timeout( this.configService.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                return this.http.post( url, body, httpOptions )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while login operator', error )
                return throwError( error )
            } )
        )
    }

    private httpAssignToEndpoint ( endpointId ) {
        const url = this.configService.assignToEndpoint
        const body = {
            endpointId: endpointId
        }

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( true, false, 'validation' ) ).pipe(
            timeout( this.configService.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                return this.http.post( url, body, httpOptions )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while assign endpoint to operator session', error )
                return throwError( error )
            } )
        )
    }

    private httpLogout () {
        const url = this.configService.logoutOperator

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( true, false, 'validation' ) ).pipe(
            timeout( this.configService.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ): any => {
                return this.http.delete( url, httpOptions )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while logout operator', error )
                return throwError( error )
            } )
        )
    }

    private getOperatorAccount ( saveInLocalStorage = false ) {
        const url = this.configService.operatorAccount
        return fromPromise( this.lbHttpService.generateHeaderForLineberty( true, false, 'validation' ) ).pipe(
            timeout( this.configService.defaultHttpTimeout ),
            mergeMap( ( httpOptions: any ) => {
                return this.http.get( url, httpOptions )
            } ),
            map( ( account ) => {
                if ( saveInLocalStorage ) {
                    this.localStorageService.setOperatorAccount( account )
                }
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while getting operator account', error )
                return throwError( error )
            } )
        )
    }

    private httpGetOperatorRoles ( saveInLocalStorage = false ) {
        const url = this.configService.operatorRoles
        return fromPromise( this.lbHttpService.generateHeaderForLineberty( true, false, 'validation' ) ).pipe(
            timeout( this.configService.defaultHttpTimeout ),
            mergeMap( ( httpOptions: any ) => {
                return this.http.get( url, httpOptions )
            } ),
            map( ( roles: any ) => {
                if ( saveInLocalStorage ) {
                    const queueId = this.localStorageService.getSelectedQueue().queueId
                    this.localStorageService.setOperatorRoles( roles.profiles[ roles.queues[ queueId ] ] )
                }
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while getting operator roles', error )
                return throwError( error )
            } )
        )
    }
}
